<template>
  <div class="am3-input-container text-sm">
    <AM3InputLabel
      :label="label"
      :required="required"
      :input-id="inputId"
    />

    <p
      v-if="subLabel"
      class="text-sm text-secondary mb-3"
    >
      {{ subLabel }}
    </p>

    <div
      class="am3-file-dropzone group flex flex-col items-center justify-center sm:min-h-[160px] focus-within:border-brand relative border-2 border-surface-weak border-dashed rounded-lg py-4 px-6 text-center transition-colors"
      :class="[
        isDragOver ? 'border-brand bg-brand-light' : '',
        disabled
          ? 'cursor-not-allowed opacity-50 bg-disabled'
          : 'hover:bg-brand-light cursor-pointer',
        validationState === false || typeof validationState === 'string'
          ? 'border-[var(--font-colour-warning)] !border-solid !border-[1px]'
          : '',
      ]"
      :data-test-id="generateDataTestId(dataTestId, 'dropzone')"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
      @click="handleDropzoneClick"
    >
      <!-- Hidden file input -->
      <input
        :id="inputId"
        ref="fileInputRef"
        type="file"
        class="sr-only"
        :accept="accept"
        :multiple="multiple"
        :disabled="disabled"
        :data-test-id="generateDataTestId(dataTestId, 'input')"
        @change="handleFileSelect"
      >

      <!-- Upload content -->
      <div
        v-if="hasNoFiles"
        class="space-y-2 mb-3"
      >
        <div
          class="size-8 object-contain my-4 rounded-md bg-surface-weak flex flex-col items-center justify-center mx-auto"
        >
          <div class="mx-auto size-8 flex items-center justify-center">
            <AM3Icon
              v-if="!isUploading"
              :type="icon"
              size="xl"
              class="text-brand"
              color="brand"
            />
            <div
              v-else
              class="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"
            />
          </div>
        </div>
        <div>
          <p class="text-primary font-medium text-sm mb-1">
            <span
              v-if="!isUploading"
              class="text-brand"
            >Upload {{ multiple ? "files" : "a file" }}</span>
            <span
              v-else
              class="text-brand"
            >Uploading...</span>
            <span v-if="!isUploading"> or drag and drop</span>
          </p>
          <p
            v-if="accept"
            class="text-xs text-secondary mt-1"
          >
            Supported formats: {{ formatAcceptedTypes }}
          </p>
          <p
            v-if="maxSize"
            class="text-xs text-secondary"
          >
            Max file size: {{ formatFileSize(parseMaxSize(maxSize)) }}
          </p>
        </div>
      </div>

      <!-- Preview URL content (for existing uploaded files) -->
      <div
        v-else-if="hasPreviewUrlOnly"
        class="space-y-3"
      >
        <div class="relative">
          <video
            v-if="isVideoUrl(singlePreviewUrl)"
            :src="singlePreviewUrl"
            :poster="videoThumbnails.get(singlePreviewUrl) || undefined"
            class="w-full h-32 object-contain rounded-md bg-surface-weak"
            muted
            loop
            playsinline
            preload="metadata"
            crossorigin="anonymous"
            @mouseenter="startVideoPreview"
            @mouseleave="stopVideoPreview"
            @pause="() => {}"
            @ended="(e) => ((e.target as HTMLVideoElement).currentTime = 0)"
          />
          <img
            v-else
            :src="singlePreviewUrl"
            alt="Uploaded image"
            class="w-full h-32 object-contain rounded-md bg-surface-weak"
          >
          <div
            class="absolute inset-0 bg-black bg-opacity-0 transition-opacity rounded-md pointer-events-none"
          />
          <button
            v-if="!disabled"
            type="button"
            class="absolute top-2 right-2 p-1 bg-surface-light bg-opacity-80 hover:bg-opacity-100 rounded-full shadow-sm size-6"
            @click.stop="removeExistingFile()"
          >
            <AM3Icon
              type="x-mark"
              size="sm"
              class="text-secondary"
            />
          </button>
          <div class="mt-2 px-2">
            <p class="text-sm font-medium text-secondary">
              {{ isVideoUrl(singlePreviewUrl) ? 'Existing video' : 'Existing image' }}
            </p>
          </div>
        </div>
        <AM3Button
          v-if="!disabled"
          type="link"
          size="xs"
          :text="isVideoUrl(singlePreviewUrl) ? 'Replace video' : 'Replace image'"
          @click.stop="handleReplaceClick"
        />
      </div>

      <!-- File preview -->
      <div
        v-else-if="hasSingleFile"
        class="space-y-3"
      >
        <div
          v-if="!multiple && modelValue"
          class="relative"
        >
          <!-- Image Preview -->
          <div
            v-if="hasSingleFile && isImageFile(modelValue as File)"
            class="relative group"
          >
            <img
              :src="getImagePreviewUrl(modelValue as File)"
              :alt="(modelValue as File).name"
              class="h-32 object-contain bg-surface-weak mx-auto rounded-md rounded-lg"
              :style="{
                width: getSingleImageWidth(modelValue as File) + 'px',
              }"
            >

            <!-- floating x-mark / cancel / remove button -->
            <button
              v-if="!disabled"
              type="button"
              class="absolute top-2 right-2 p-1 bg-surface-light bg-opacity-80 hover:bg-opacity-100 rounded-full shadow-sm size-6"
              @click.stop="removeFile()"
            >
              <AM3Icon
                type="x-mark"
                size="sm"
                class="text-secondary"
              />
            </button>
            <div class="mt-2 px-2 min-w-0">
              <p
                class="text-sm font-medium text-primary truncate max-w-[25rem] mx-auto"
              >
                {{ (modelValue as File).name }}
              </p>
              <p class="text-xs text-secondary">
                {{ formatFileSize((modelValue as File).size) }}
              </p>
            </div>
          </div>

          <!-- Video Preview -->
          <div
            v-else-if="hasSingleFile && isVideoFile(modelValue as File)"
            class="relative group"
          >
            <video
              :src="singleVideoUrl || ''"
              :poster="singleVideoThumbnail || ''"
              class="w-full h-32 object-contain rounded-md bg-surface-weak"
              muted
              loop
              playsinline
              preload="metadata"
              crossorigin="anonymous"
              @mouseenter="startVideoPreview"
              @mouseleave="stopVideoPreview"
              @pause="() => {}"
              @ended="(e) => ((e.target as HTMLVideoElement).currentTime = 0)"
            />
            <div
              class="absolute inset-0 bg-black bg-opacity-0 transition-opacity rounded-md pointer-events-none"
            />

            <button
              v-if="!disabled"
              type="button"
              class="absolute top-2 right-2 p-1 translate-x-12 duration-100 focus:duration-0 transition-transform group-hover:translate-x-0 focus:translate-x-0 outline outline-transparent focus:outline-brand outline-1 bg-surface-light hover:bg-brand-light bg-opacity-80 hover:bg-opacity-100 rounded-full shadow-sm size-6"
              @click.stop="removeFile()"
            >
              <AM3Icon
                type="x-mark"
                size="sm"
                class="text-secondary"
              />
            </button>
            <div class="mt-2 px-2 min-w-0">
              <p
                class="text-sm font-medium text-primary truncate max-w-[25rem] mx-auto"
              >
                {{ (modelValue as File).name }}
              </p>
              <p class="text-xs text-secondary">
                {{ formatFileSize((modelValue as File).size) }}
              </p>
            </div>
          </div>

          <!-- Non-Image/Video File Preview -->
          <div
            v-else
            class="flex items-center justify-between p-3 bg-surface-weak rounded-md"
          >
            <div class="flex items-center space-x-3">
              <div
                class="w-8 h-8 flex items-center justify-center rounded bg-brand-light"
              >
                <AM3Icon
                  type="document"
                  size="sm"
                  class="text-brand"
                />
              </div>
              <div class="flex-1 min-w-0">
                <p
                  class="text-sm font-medium text-primary truncate max-w-[300px]"
                >
                  {{ (modelValue as File).name }}
                </p>
                <p class="text-xs text-secondary">
                  {{ formatFileSize((modelValue as File).size) }}
                </p>
              </div>
            </div>
            <button
              v-if="!disabled"
              type="button"
              class="hover:bg-brand-light shadow-sm bg-surface-light flex flex-col items-center justify-center hover:text-brand rounded-full size-6"
              @click.stop="removeFile()"
            >
              <AM3Icon
                type="x-mark"
                size="sm"
                class="text-secondary"
              />
            </button>
          </div>
        </div>

        <div
          v-if="multiple && Array.isArray(modelValue)"
          class="space-y-2"
        >
          <div
            v-for="(file, index) in modelValue"
            :key="`${file.name}-${index}`"
            class="relative"
          >
            <!-- Image Preview for Multiple -->
            <div
              v-if="isImageFile(file)"
              class="relative group"
            >
              <img
                :src="getImagePreviewUrl(file)"
                :alt="file.name"
                class="w-full h-24 object-contain rounded-md bg-surface-weak"
              >
              <div
                class="absolute inset-0 bg-black bg-opacity-0 transition-opacity rounded-md"
              />
              <button
                v-if="!disabled"
                type="button"
                class="absolute top-2 right-2 p-1 bg-surface-light bg-opacity-80 hover:bg-opacity-100 rounded-full shadow-sm"
                @click.stop="removeFile(index)"
              >
                <AM3Icon
                  type="x-mark"
                  size="sm"
                  class="text-secondary"
                />
              </button>
              <div class="mt-1 px-2 min-w-0">
                <p
                  class="text-xs font-medium text-secondary truncate max-w-[300px]"
                >
                  {{ file.name }}
                </p>
                <p class="text-xs text-secondary">
                  {{ formatFileSize(file.size) }}
                </p>
              </div>
            </div>

            <!-- Non-Image File Preview for Multiple -->
            <div
              v-else
              class="flex items-center justify-between p-3 bg-surface-weak rounded-md"
            >
              <div class="flex items-center space-x-3">
                <div
                  class="w-8 h-8 flex items-center justify-center rounded bg-brand-light"
                >
                  <AM3Icon
                    type="document"
                    size="sm"
                    class="text-brand"
                  />
                </div>
                <div class="flex-1 min-w-0">
                  <p
                    class="text-sm font-medium text-secondary truncate max-w-[300px]"
                  >
                    {{ file.name }}
                  </p>
                  <p class="text-xs text-secondary">
                    {{ formatFileSize(file.size) }}
                  </p>
                </div>
              </div>
              <button
                v-if="!disabled"
                type="button"
                class="p-1 hover:bg-gray-200 rounded"
                @click.stop="removeFile(index)"
              >
                <AM3Icon
                  type="x-mark"
                  size="sm"
                  class="text-secondary"
                />
              </button>
            </div>
          </div>
        </div>
        <AM3Button
          v-if="!disabled"
          type="link"
          size="xs"
          :text="multiple ? 'Add more files' : 'Replace file'"
          @click.stop="handleReplaceClick"
        />
      </div>

      <!-- Single URL preview (after upload) -->
      <div
        v-else-if="hasSingleUrl"
        class="space-y-3"
      >
        <div class="relative">
          <!-- Video Preview for URL -->
          <div
            v-if="isVideoUrl(modelValue as string)"
            class="relative group"
          >
            <video
              :src="modelValue as string"
              :poster="videoThumbnails.get(modelValue as string) || undefined"
              class="w-full h-32 object-contain rounded-md bg-surface-weak"
              muted
              loop
              playsinline
              preload="metadata"
              crossorigin="anonymous"
              @mouseenter="startVideoPreview"
              @mouseleave="stopVideoPreview"
              @pause="() => {}"
              @ended="(e) => ((e.target as HTMLVideoElement).currentTime = 0)"
            />
            <div
              class="absolute inset-0 bg-black bg-opacity-0 transition-opacity rounded-md pointer-events-none"
            />

            <button
              v-if="!disabled"
              type="button"
              class="absolute top-2 right-2 p-1 bg-surface-light bg-opacity-80 hover:bg-opacity-100 rounded-full shadow-sm size-6"
              @click.stop="removeFile()"
            >
              <AM3Icon
                type="x-mark"
                size="sm"
                class="text-secondary"
              />
            </button>
            <div class="mt-2 px-2 min-w-0">
              <p class="text-sm font-medium text-primary">
                Uploaded video
              </p>
              <p
                v-if="getUploadedUrlSize(modelValue as string) !== null"
                class="text-xs text-secondary"
              >
                {{
                  formatFileSize(
                    getUploadedUrlSize(modelValue as string) as number,
                  )
                }}
              </p>
            </div>
          </div>

          <!-- Image Preview for URL -->
          <div
            v-else
            class="relative group"
          >
            <img
              :src="modelValue as string"
              alt="Uploaded image"
              class="h-32 object-contain bg-surface-weak mx-auto rounded-md"
            >
            <div
              class="absolute inset-0 bg-black bg-opacity-0 transition-opacity rounded-md pointer-events-none"
            />

            <button
              v-if="!disabled"
              type="button"
              class="absolute top-2 right-2 p-1 bg-surface-light bg-opacity-80 hover:bg-opacity-100 rounded-full shadow-sm size-6"
              @click.stop="removeFile()"
            >
              <AM3Icon
                type="x-mark"
                size="sm"
                class="text-secondary"
              />
            </button>
            <div class="mt-2 px-2 min-w-0">
              <p class="text-sm font-medium text-primary">
                Uploaded image
              </p>
              <p
                v-if="getUploadedUrlSize(modelValue as string) !== null"
                class="text-xs text-secondary"
              >
                {{
                  formatFileSize(
                    getUploadedUrlSize(modelValue as string) as number,
                  )
                }}
              </p>
            </div>
          </div>
        </div>
        <AM3Button
          v-if="!disabled"
          type="link"
          size="xs"
          text="Replace file"
          @click.stop="handleReplaceClick"
        />
      </div>

      <!-- Multiple files preview -->
      <div
        v-else-if="hasMultipleFiles"
        class="flex flex-wrap gap-3"
      >
        <!-- New Files (pending upload) -->
        <div
          v-for="(file, index) in pendingFiles"
          :key="`${file.name}-${index}`"
          class="relative flex-shrink-0"
        >
          <!-- Image Preview for Multiple -->
          <div
            v-if="isImageFile(file)"
            class="relative group w-20 rounded-xl overflow-hidden bg-surface-weak"
            :style="{ aspectRatio: `1 / ${previewAspectRatio}` }"
          >
            <img
              :src="getImagePreviewUrl(file)"
              :alt="file.name"
              class="w-full h-full object-cover"
            >
            <div
              class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity pointer-events-none"
            />
            <button
              v-if="!disabled"
              type="button"
              class="absolute top-2 right-2 p-1 bg-surface-light bg-opacity-80 hover:bg-opacity-100 rounded-full shadow-sm size-6"
              @click.stop="removeFile(index)"
            >
              <AM3Icon
                type="x-mark"
                size="sm"
                class="text-secondary"
              />
            </button>
          </div>

          <!-- Video Preview for Multiple -->
          <div
            v-else-if="isVideoFile(file)"
            class="relative group w-20 rounded-xl overflow-hidden bg-surface-weak"
            :style="{ aspectRatio: `1 / ${previewAspectRatio}` }"
          >
            <video
              :src="getVideoPreviewUrl(file)"
              :poster="getVideoThumbnailForFile(file) || undefined"
              class="w-full h-full object-cover"
              muted
              loop
              playsinline
              preload="metadata"
              @mouseenter="startVideoPreview"
              @mouseleave="stopVideoPreview"
              @pause="() => {}"
              @ended="(e) => ((e.target as HTMLVideoElement).currentTime = 0)"
            />
            <div
              class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity pointer-events-none"
            />
            <button
              v-if="!disabled"
              type="button"
              class="absolute top-2 right-2 p-1 bg-surface-light bg-opacity-80 hover:bg-opacity-100 rounded-full shadow-sm size-6"
              @click.stop="removeFile(index)"
            >
              <AM3Icon
                type="x-mark"
                size="sm"
                class="text-secondary"
              />
            </button>
          </div>

          <!-- Non-Image/Video File Preview for Multiple -->
          <div
            v-else
            class="relative group w-20 flex flex-col items-center justify-center bg-surface-weak rounded-xl border"
            :style="{ aspectRatio: `1 / ${previewAspectRatio}` }"
          >
            <div
              class="w-6 h-6 flex items-center justify-center rounded bg-brand-light mb-1"
            >
              <AM3Icon
                type="document"
                size="sm"
                class="text-brand"
              />
            </div>
            <p
              class="text-xs font-medium text-primary truncate max-w-[60px] text-center"
            >
              {{ file?.name?.split(".")?.pop()?.toUpperCase() || "FILE" }}
            </p>
            <button
              v-if="!disabled"
              type="button"
              class="absolute top-2 right-2 p-1 bg-surface-light bg-opacity-80 hover:bg-opacity-100 rounded-full shadow-sm size-6"
              @click.stop="removeFile(index)"
            >
              <AM3Icon
                type="x-mark"
                size="sm"
                class="text-secondary"
              />
            </button>
          </div>
        </div>

        <!-- Uploading Files (with progress) -->
        <div
          v-for="(uploadInfo, index) in uploadingFilesArray"
          :key="`uploading-${uploadInfo.file.name}-${index}`"
          class="relative flex-shrink-0"
        >
          <div
            class="relative group w-20 rounded-xl overflow-hidden bg-surface-weak"
            :style="{ aspectRatio: `1 / ${previewAspectRatio}` }"
          >
            <!-- Background image/video for uploading files -->
            <template v-if="isImageFile(uploadInfo.file)">
              <img
                :src="getImagePreviewUrl(uploadInfo.file)"
                :alt="uploadInfo.file.name"
                class="w-full h-full object-cover opacity-50"
              >
            </template>
            <template v-else-if="isVideoFile(uploadInfo.file)">
              <div
                class="w-full h-full relative bg-surface-dark flex items-center justify-center opacity-50"
              >
                <!-- Show thumbnail if available, otherwise show play icon -->
                <template v-if="uploadInfo.thumbnail">
                  <img
                    :src="uploadInfo.thumbnail"
                    :alt="uploadInfo.file.name"
                    class="w-full h-full object-cover"
                  >
                </template>
                <template v-else-if="uploadInfo.videoUrl">
                  <!-- Show video if URL available but thumbnail still loading -->
                  <video
                    :src="uploadInfo.videoUrl"
                    :poster="uploadInfo.thumbnail"
                    class="w-full h-full object-cover"
                    muted
                    playsinline
                    preload="metadata"
                  />
                </template>
                <template v-else>
                  <!-- Fallback play icon while everything loads -->
                  <div
                    class="w-8 h-8 flex items-center justify-center rounded-full bg-surface-light bg-opacity-20"
                  >
                    <AM3Icon
                      type="play"
                      size="sm"
                      class="text-secondary ml-0.5"
                    />
                  </div>
                </template>
              </div>
            </template>
            <template v-else>
              <div
                class="w-full h-full flex flex-col items-center justify-center bg-surface-weak opacity-50"
              >
                <div
                  class="w-6 h-6 flex items-center justify-center rounded bg-brand-light mb-1"
                >
                  <AM3Icon
                    type="document"
                    size="sm"
                    class="text-brand"
                  />
                </div>
                <p
                  class="text-xs font-medium text-primary truncate max-w-[60px] text-center"
                >
                  {{
                    uploadInfo.file.name.split(".").pop()?.toUpperCase()
                      || "FILE"
                  }}
                </p>
              </div>
            </template>

            <!-- Progress overlay -->
            <div class="absolute inset-0">
              <!-- Progress fill from bottom to top -->
              <div
                class="absolute bottom-0 left-0 right-0 bg-brand bg-opacity-20 transition-all duration-75 ease-out"
                :style="{ height: `${uploadInfo.progress}%` }"
              />

              <!-- Loading spinner and percentage -->
              <div
                class="absolute inset-0 flex flex-col items-center justify-center"
              >
                <div class="mb-1">
                  <div
                    class="animate-spin rounded-full h-6 w-6 border-2 border-brand border-t-transparent"
                  />
                </div>
                <div class="bg-black bg-opacity-60 rounded px-1 py-0.5">
                  <span class="text-xs text-white font-medium">{{ Math.round(uploadInfo.progress) }}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- All Preview URLs (from prop + internally uploaded images) -->
        <div
          v-for="(url, index) in internalPreviewUrls"
          :key="`internal-${index}`"
          class="relative flex-shrink-0"
        >
          <div
            class="relative group w-20 rounded-xl overflow-hidden bg-surface-weak"
            :style="{ aspectRatio: `1 / ${previewAspectRatio}` }"
          >
            <!-- Video Preview for URLs -->
            <video
              v-if="isVideoUrl(url)"
              :src="url"
              :poster="videoThumbnails.get(url) || undefined"
              class="w-full h-full object-cover"
              muted
              loop
              playsinline
              preload="metadata"
              crossorigin="anonymous"
              @mouseenter="startVideoPreview"
              @mouseleave="stopVideoPreview"
              @pause="() => {}"
              @ended="(e) => ((e.target as HTMLVideoElement).currentTime = 0)"
            />
            <!-- Image Preview for URLs -->
            <img
              v-else
              :src="url"
              :alt="`Uploaded image ${index + 1}`"
              class="w-full h-full object-cover"
            >
            <div
              class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity pointer-events-none"
            />
            <button
              v-if="!disabled"
              type="button"
              class="absolute top-2 right-2 p-1 bg-surface-light bg-opacity-80 hover:bg-opacity-100 rounded-full shadow-sm size-6"
              @click.stop="removeInternalUrl(index)"
            >
              <AM3Icon
                type="x-mark"
                size="sm"
                class="text-secondary"
              />
            </button>
          </div>
        </div>

        <!-- Uploaded Videos -->
        <div
          v-for="(videoUrl, index) in uploadedVideoUrls"
          :key="`video-${index}`"
          class="relative flex-shrink-0"
        >
          <div
            class="relative group w-20 rounded-xl overflow-hidden bg-surface-weak"
            :style="{ aspectRatio: `1 / ${previewAspectRatio}` }"
          >
            <video
              :src="videoUrl"
              :poster="videoThumbnails.get(videoUrl) || undefined"
              class="w-full h-full object-cover"
              muted
              loop
              playsinline
              preload="metadata"
              crossorigin="anonymous"
              @mouseenter="startVideoPreview"
              @mouseleave="stopVideoPreview"
              @pause="() => {}"
              @ended="(e) => ((e.target as HTMLVideoElement).currentTime = 0)"
            />
            <div
              class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity pointer-events-none"
            />
            <button
              v-if="!disabled"
              type="button"
              class="absolute top-2 right-2 p-1 bg-surface-light bg-opacity-80 hover:bg-opacity-100 rounded-full shadow-sm size-6"
              @click.stop="removeUploadedVideo(index)"
            >
              <AM3Icon
                type="x-mark"
                size="sm"
                class="text-secondary"
              />
            </button>
          </div>
        </div>

        <!-- Add More Button -->
        <button
          v-if="!disabled"
          type="button"
          class="w-20 border-2 border-dashed border-surface-weak hover:border-brand rounded-xl flex items-center justify-center group transition-colors"
          :style="{ aspectRatio: `1 / ${previewAspectRatio}` }"
          @click.stop="handleReplaceClick"
        >
          <AM3Icon
            type="plus"
            size="lg"
            class="text-secondary group-hover:text-brand transition-colors"
          />
        </button>
      </div>
    </div>

    <AM3InputError
      :error="validationState"
      :data-test-id="generateDataTestId(dataTestId, 'input-error')"
    />

    <!-- Image Reframing Modal -->
    <AM3Modal
      v-model:is-open="showReframeModal"
      :header="
        reframeQueue.length > 0
          ? `Upload artwork (${currentReframeIndex + 1} of ${reframeQueue.length})`
          : 'Upload artwork'
      "
      size="lg"
      teleport-to="body"
    >
      <template #body>
        <div class="space-y-4">
          <p class="text-sm">
            Drag the purple frame to reposition your crop. Scroll to zoom in
            or out.
          </p>

          <div
            class="relative border border-surface-dark rounded-lg overflow-hidden bg-surface-weak"
          >
            <canvas
              ref="cropCanvas"
              :class="[
                'w-full h-auto',
                isDragging ? 'cursor-grabbing' : 'cursor-grab',
              ]"
              @mousedown="startDrag"
              @mousemove="updateDrag"
              @mouseup="endDrag"
              @mouseleave="endDrag"
              @wheel.prevent="handleZoom"
            />

            <!-- Zoom controls -->
            <div class="absolute top-2 right-2 flex flex-col gap-1">
              <button
                type="button"
                class="size-8 bg-surface-light hover:bg-surface-weak border border-surface-dark rounded shadow-sm flex items-center justify-center"
                @click="zoomIn"
              >
                <AM3Icon
                  type="plus"
                  size="sm"
                  class="text-secondary"
                />
              </button>
              <button
                type="button"
                class="size-8 bg-surface-light hover:bg-surface-weak border border-surface-dark rounded shadow-sm flex items-center justify-center"
                @click="zoomOut"
              >
                <AM3Icon
                  type="minus"
                  size="sm"
                  class="text-secondary"
                />
              </button>
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-end space-x-3">
          <AM3Button
            text="Cancel"
            type="tertiary"
            @click="closeReframeModal"
          />
          <AM3Button
            text="Apply Crop"
            type="primary"
            @click="applyCrop"
          />
        </div>
      </template>
    </AM3Modal>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import {
  defineComponent,
  ref,
  computed,
  onUnmounted,
  watch,
  nextTick,
} from 'vue'
import { v4 as uuidv4 } from 'uuid'
import '@/components/am3-generic/core/theme.css'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'
import { useFormValidation } from '@/components/am3-generic/inputs/useFormValidation'
import AM3InputError from '@/components/am3-generic/inputs/AM3InputError.vue'
import AM3InputLabel from '@/components/am3-generic/inputs/AM3InputLabel.vue'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import AM3Modal from '@/components/am3-generic/layout/AM3Modal.vue'
import AM3Button from '@/components/am3-generic/base/AM3Button.vue'
import { getContext } from '@/utils/general'

/**
 * AM3InputFile component
 *
 * A file upload dropzone component with drag & drop, validation, and preview
 */
export default defineComponent({
  name: 'AM3InputFile',
  components: { AM3InputError, AM3InputLabel, AM3Icon, AM3Modal, AM3Button },

  props: {
    // v-model value - File or File[] (accepts empty string from parent)
    modelValue: {
      type: [File, Array, String] as PropType<File | File[] | string | null>,
      default: null,
    },

    // Input label (string or false to hide)
    label: {
      type: [String, Boolean] as PropType<string | false>,
      default: '',
    },

    // Sub-label text displayed below the main label
    subLabel: {
      type: String,
      default: '',
    },

    // Accepted file types (e.g., "image/*", ".pdf,.doc")
    accept: {
      type: String,
      default: '',
    },

    // Allow multiple files
    multiple: {
      type: Boolean,
      default: false,
    },

    // Maximum file size in bytes or string like "4MB"
    maxSize: {
      type: [Number, String],
      default: 0, // 0 = no limit
    },

    // Disabled state
    disabled: {
      type: Boolean,
      default: false,
    },

    // Required field
    required: {
      type: Boolean,
      default: false,
    },

    // Validation function
    validate: {
      type: Function as PropType<
        (value: File | File[] | null) => boolean | string
      >,
      default: () => true,
    },

    // Validate on file change
    validateOnChange: {
      type: Boolean,
      default: true,
    },

    // Icon for the upload area
    icon: {
      type: String,
      default: 'photo',
    },

    // Data test id prefix
    dataTestId: {
      type: String,
      default: props => props.label || 'file-input',
    },

    // Auto-upload configuration
    autoUpload: {
      type: Boolean,
      default: false,
    },

    // Asset type for auto-upload (e.g., 'campaign-image')
    assetType: {
      type: String,
      default: 'asset',
    },

    // Preview URL for existing uploaded files (string for single, array for multiple)
    previewUrl: {
      type: [String, Array] as PropType<string | string[]>,
      default: '',
    },

    // Enable image reframing/cropping
    reframe: {
      type: Boolean,
      default: false,
    },

    // Frame dimensions (e.g., '600x420' for resolution or '9:16' for ratio)
    frame: {
      type: String,
      default: '',
    },
  },

  emits: [
    'update:modelValue',
    'update:previewUrl',
    'change',
    'upload-success',
    'upload-error',
    'reframe-complete',
  ],

  setup(props, { emit }) {
    // Generate unique ID for the input
    const inputId = ref(`am3-file-${uuidv4()}`)
    const fileInputRef = ref<HTMLInputElement | null>(null)
    const isDragOver = ref(false)
    const objectUrls = ref<Set<string>>(new Set())
    const isUploading = ref(false)
    const uploadingFiles = ref<
      Map<
        string,
        { progress: number; file: File; thumbnail?: string; videoUrl?: string }
      >
    >(new Map())
    const uploadedFileUrls = ref<string[]>([])
    const uploadedVideoUrls = ref<string[]>([])
    const uploadedUrlSizes = ref<Map<string, number>>(new Map())
    const videoThumbnails = ref<Map<string, string>>(new Map())
    const reframeQueue = ref<File[]>([])
    const currentReframeIndex = ref(0)

    // Reframing/cropping variables
    const showReframeModal = ref(false)
    const cropCanvas = ref<HTMLCanvasElement | null>(null)
    const originalFile = ref<File | null>(null)
    const originalImage = ref<HTMLImageElement | null>(null)
    const originalImageDimensions = ref<{
      width: number;
      height: number;
    } | null>(null)
    const cropArea = ref({ x: 0, y: 0, width: 0, height: 0 })
    const isDragging = ref(false)
    const dragMode = ref<'crop' | 'image'>('crop') // Track what we're dragging
    const dragStart = ref({ x: 0, y: 0 })
    const imageScale = ref(1)
    const imageOffset = ref({ x: 0, y: 0 })
    const canvasSize = ref({ width: 600, height: 400 })

    // Setup form validation
    const {
      validationState,
      runValidation: _runValidation,
      resetValidation,
    } = useFormValidation(props.validate, props.modelValue, props.required)

    // Computed properties
    const formatAcceptedTypes = computed(() => {
      if (!props.accept) return ''
      return props.accept.replace(/\./g, '').replace(/,/g, ', ').toUpperCase()
    })

    // Computed properties for template state management
    const hasNoFiles = computed(() => {
      return (
        (!props.modelValue
          || props.modelValue === ''
          || (Array.isArray(props.modelValue) && props.modelValue.length === 0))
        && (!props.previewUrl
          || (Array.isArray(props.previewUrl) && props.previewUrl.length === 0))
      )
    })

    const hasPreviewUrlOnly = computed(() => {
      return (
        !props.multiple
        && props.previewUrl
        && (!props.modelValue
          || props.modelValue === ''
          || (Array.isArray(props.modelValue) && props.modelValue.length === 0))
      )
    })

    const singlePreviewUrl = computed(() => {
      return typeof props.previewUrl === 'string'
        ? props.previewUrl
        : (props.previewUrl as string[])[0]
    })

    const hasSingleFile = computed(() => {
      return (
        !props.multiple
        && props.modelValue
        && props.modelValue !== ''
        && !Array.isArray(props.modelValue)
        && props.modelValue instanceof File
      )
    })

    const hasSingleUrl = computed(() => {
      return (
        !props.multiple
        && props.modelValue
        && props.modelValue !== ''
        && !Array.isArray(props.modelValue)
        && typeof props.modelValue === 'string'
      )
    })

    const hasMultipleFiles = computed(() => {
      return (
        props.multiple
        && (pendingFiles.value.length > 0
          || uploadingFilesArray.value.length > 0
          || internalPreviewUrls.value.length > 0
          || uploadedVideoUrls.value.length > 0)
      )
    })

    // All preview URLs - combines external previewUrl prop with internally uploaded URLs
    // Also includes URLs from modelValue when auto-upload is enabled
    // Excludes video URLs that are tracked in uploadedVideoUrls to prevent duplicates
    const internalPreviewUrls = computed(() => {
      const allUrls: string[] = []

      // Add URLs from previewUrl prop (existing uploaded files) but exclude videos
      if (Array.isArray(props.previewUrl)) {
        allUrls.push(
          ...props.previewUrl.filter(
            url => !uploadedVideoUrls.value.includes(url),
          ),
        )
      }
      else if (typeof props.previewUrl === 'string' && props.previewUrl) {
        // Only add if it's not a video URL that's already been uploaded
        if (!uploadedVideoUrls.value.includes(props.previewUrl)) {
          allUrls.push(props.previewUrl)
        }
      }

      // Add URLs from modelValue (when auto-upload converts Files to URLs)
      if (props.autoUpload && Array.isArray(props.modelValue)) {
        props.modelValue.forEach((item) => {
          if (
            typeof item === 'string'
            && !allUrls.includes(item)
            && !uploadedVideoUrls.value.includes(item)
          ) {
            allUrls.push(item)
          }
        })
      }

      // Add internally tracked uploaded URLs (avoid duplicates) but exclude videos
      uploadedFileUrls.value.forEach((url) => {
        if (!allUrls.includes(url) && !uploadedVideoUrls.value.includes(url)) {
          allUrls.push(url)
        }
      })

      return allUrls
    })

    // Files currently being uploaded with progress
    const uploadingFilesArray = computed(() => {
      return Array.from(uploadingFiles.value.values())
    })

    // Calculate aspect ratio for preview thumbnails based on frame
    const previewAspectRatio = computed(() => {
      if (!props.frame) return 1 // Square by default

      // Handle ratio format like "16:9" or "9:16"
      if (props.frame.includes(':')) {
        const [w, h] = props.frame.split(':').map(Number)
        return h / w // Height / Width for aspect-ratio CSS
      }

      // Handle pixel format like "600x420"
      if (props.frame.includes('x')) {
        const [w, h] = props.frame.split('x').map(Number)
        return h / w // Height / Width for aspect-ratio CSS
      }

      return 1 // Default to square
    })

    // Only show files that haven't been uploaded yet (for auto-upload mode)
    const pendingFiles = computed(() => {
      if (!Array.isArray(props.modelValue) || !props.modelValue) return []

      // If auto-upload is disabled, show all files
      if (!props.autoUpload) return props.modelValue

      // If auto-upload is enabled, only show File objects (not uploaded URLs)
      // Don't show files that are currently uploading either
      const uploadingFileNames = Array.from(uploadingFiles.value.values()).map(
        item => item.file.name,
      )
      return props.modelValue.filter((item) => {
        // Only show if it's a File object (not a URL string) and not currently uploading
        return item instanceof File && !uploadingFileNames.includes(item.name)
      })
    })

    // Get the correct video URL for single file mode (S3 URL if uploaded, blob URL if pending)
    const singleVideoUrl = computed(() => {
      if (
        !props.modelValue
        || props.modelValue === ''
        || Array.isArray(props.modelValue)
      ) {
        return null
      }

      const file = props.modelValue as File & {
        _uploaded?: boolean;
        _s3Url?: string;
      }

      if (!isVideoFile(file)) {
        return null
      }

      // Check if this video has been uploaded (has S3 URL in modelValue)
      if (file._uploaded && file._s3Url) {
        return file._s3Url
      }

      // Fallback to blob URL for pending upload
      return getVideoPreviewUrl(file)
    })

    // Get the correct video thumbnail for single file mode
    const singleVideoThumbnail = computed(() => {
      const videoUrl = singleVideoUrl.value
      if (!videoUrl) return null

      // First try to get from uploaded video thumbnails (S3 URLs)
      const uploadedThumbnail = videoThumbnails.value.get(videoUrl)
      if (uploadedThumbnail) {
        return uploadedThumbnail
      }

      // For pending videos, check the thumbnails by file
      if (
        !props.modelValue
        || Array.isArray(props.modelValue)
        || props.modelValue === ''
      )
        return null
      const file = props.modelValue as File
      if (file && isVideoFile(file)) {
        return getVideoThumbnailForFile(file)
      }

      return null
    })

    // Parse frame dimensions
    const frameWidth = computed(() => {
      if (!props.frame) return 600 // default width

      // Handle ratio format like "16:9" or "9:16"
      if (props.frame.includes(':')) {
        const [w, h] = props.frame.split(':').map(Number)

        // If we have original image dimensions, calculate max size for this ratio
        if (originalImageDimensions.value) {
          const { width: imgWidth, height: imgHeight }
            = originalImageDimensions.value
          const targetRatio = w / h
          const imageRatio = imgWidth / imgHeight

          // Calculate the largest dimensions that fit the target ratio
          if (imageRatio > targetRatio) {
            // Image is wider, constrain by height
            return Math.round(imgHeight * targetRatio)
          }
          else {
            // Image is taller or same ratio, constrain by width
            return imgWidth
          }
        }

        // Fallback: Use a base width of 450px if no image dimensions available yet
        const baseWidth = 450
        return baseWidth
      }

      // Handle pixel format like "600x420"
      if (props.frame.includes('x')) {
        const [width] = props.frame.split('x').map(Number)
        return width || 600
      }

      return 600
    })

    const frameHeight = computed(() => {
      if (!props.frame) return 400 // default height

      // Handle ratio format like "16:9" or "9:16"
      if (props.frame.includes(':')) {
        const [w, h] = props.frame.split(':').map(Number)

        // If we have original image dimensions, calculate max size for this ratio
        if (originalImageDimensions.value) {
          const { width: imgWidth, height: imgHeight }
            = originalImageDimensions.value
          const targetRatio = w / h
          const imageRatio = imgWidth / imgHeight

          // Calculate the largest dimensions that fit the target ratio
          if (imageRatio > targetRatio) {
            // Image is wider, constrain by height
            return imgHeight
          }
          else {
            // Image is taller or same ratio, constrain by width
            return Math.round(imgWidth * (h / w))
          }
        }

        // Fallback: Use a base width of 450px if no image dimensions available yet
        const baseWidth = 450
        return Math.round(baseWidth * (h / w))
      }

      // Handle pixel format like "600x420"
      if (props.frame.includes('x')) {
        const [, height] = props.frame.split('x').map(Number)
        return height || 400
      }

      return 400
    })

    const isImageFile = (file: File): boolean => {
      return file?.type?.startsWith('image/')
    }

    const isVideoFile = (file: File): boolean => {
      return file?.type?.startsWith('video/')
    }

    const isVideoUrl = (url: string): boolean => {
      // Check if url is a valid string
      if (!url || typeof url !== 'string') {
        return false
      }
      // Check file extension for common video formats
      const videoExtensions = [
        '.mp4',
        '.mov',
        '.avi',
        '.mkv',
        '.webm',
        '.ogg',
        '.m4v',
      ]
      const urlLower = url.toLowerCase()
      return videoExtensions.some(ext => urlLower.includes(ext))
    }

    const getImagePreviewUrl = (file: File): string => {
      const url = URL.createObjectURL(file)
      objectUrls.value.add(url)
      return url
    }

    const getVideoThumbnail = (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const video = document.createElement('video')
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          reject(new Error('Failed to get canvas context'))
          return
        }

        video.addEventListener('loadedmetadata', () => {
          // Set canvas dimensions based on video
          canvas.width = video.videoWidth
          canvas.height = video.videoHeight

          // Seek to 1 second (or duration/4 if video is shorter)
          video.currentTime = Math.min(1, video.duration / 4)
        })

        video.addEventListener('seeked', () => {
          // Draw the video frame to canvas
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

          // Convert canvas to blob and create URL
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const thumbnailUrl = URL.createObjectURL(blob)
                objectUrls.value.add(thumbnailUrl)
                resolve(thumbnailUrl)
              }
              else {
                reject(new Error('Failed to create thumbnail blob'))
              }
            },
            'image/jpeg',
            0.8,
          )

          // Clean up video element
          URL.revokeObjectURL(video.src)
        })

        video.addEventListener('error', () => {
          reject(new Error('Failed to load video'))
          URL.revokeObjectURL(video.src)
        })

        // Set video source and load
        video.src = URL.createObjectURL(file)
        video.load()
      })
    }

    const getVideoPreviewUrl = (file: File): string => {
      const fileUrl = URL.createObjectURL(file)
      objectUrls.value.add(fileUrl)
      return fileUrl
    }

    // Get or generate thumbnail for a video file (unified system)
    const getVideoThumbnailForFile = (file: File): string | null => {
      const fileKey = `pending:${file.name}-${file.size}-${file.lastModified}`

      // Return existing thumbnail if available
      if (videoThumbnails.value.has(fileKey)) {
        return videoThumbnails.value.get(fileKey)!
      }

      // Generate thumbnail asynchronously
      getVideoThumbnail(file)
        .then((thumbnailUrl) => {
          videoThumbnails.value.set(fileKey, thumbnailUrl)
        })
        .catch(() => {
          // Ignore thumbnail generation errors
        })

      return null // Return null while thumbnail is being generated
    }

    // Track image dimensions for width calculation
    const imageDimensions = ref<Map<string, { width: number; height: number }>>(
      new Map(),
    )

    const getSingleImageWidth = (file: File): number => {
      const fileKey = `${file.name}-${file.size}-${file.lastModified}`
      const dimensions = imageDimensions.value.get(fileKey)

      if (dimensions) {
        const aspectRatio = dimensions.width / dimensions.height
        const maxHeight = 128
        return Math.round(maxHeight * aspectRatio)
      }

      // If dimensions not loaded yet, load them asynchronously and return default
      const img = new Image()
      img.onload = () => {
        imageDimensions.value.set(fileKey, {
          width: img.width,
          height: img.height,
        })
      }
      img.src = getImagePreviewUrl(file)

      return 128 // Default width while loading
    }

    const startVideoPreview = (event: Event) => {
      const video = event.target as HTMLVideoElement
      if (video && video.tagName === 'VIDEO' && video.src) {
        // Don't reset currentTime - continue from where it stopped
        video.play().catch(() => {
          // Silently handle video play failures
        })
      }
    }

    const stopVideoPreview = (event: Event) => {
      const video = event.target as HTMLVideoElement
      if (video && video.tagName === 'VIDEO') {
        video.pause()
        // Don't reset currentTime - keep the current position for next hover
      }
    }

    // Cleanup function
    const revokeObjectUrls = () => {
      objectUrls.value.forEach(url => URL.revokeObjectURL(url))
      objectUrls.value.clear()
    }

    // Utility functions
    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const getUploadedUrlSize = (url: string): number | null => {
      if (!url) return null
      return uploadedUrlSizes.value.get(url) ?? null
    }

    const parseMaxSize = (maxSize: number | string): number => {
      if (typeof maxSize === 'number') return maxSize
      if (typeof maxSize === 'string' && maxSize) {
        const match = maxSize.match(/^(\d+(?:\.\d+)?)\s*(MB|KB|GB|B)?$/i)
        if (match) {
          const value = parseFloat(match[1])
          const unit = (match[2] || 'B').toUpperCase()
          switch (unit) {
            case 'GB':
              return value * 1024 * 1024 * 1024
            case 'MB':
              return value * 1024 * 1024
            case 'KB':
              return value * 1024
            case 'B':
              return value
            default:
              return value
          }
        }
      }
      return 0
    }

    const getFileSizeLimit = (file: File): number => {
      const isImage = file.type.startsWith('image/')
      const parsedMaxSize = parseMaxSize(props.maxSize)
      return isImage
        ? 5 * 1024 * 1024
        : parsedMaxSize > 0
          ? parsedMaxSize
          : Infinity
    }

    const getFileSizeErrorMessage = (file: File): string => {
      const isImage = file.type.startsWith('image/')
      const parsedMaxSize = parseMaxSize(props.maxSize)
      return isImage
        ? `File too large (${formatFileSize(file.size)}). Please upload an image under 5MB.`
        : `File too large (${formatFileSize(file.size)}). Maximum size is ${formatFileSize(parsedMaxSize)}.`
    }

    const canvasToFile = async (
      canvas: HTMLCanvasElement,
      fileName: string,
      fileType: string,
      quality: number,
    ): Promise<File | null> => {
      return await new Promise((resolve) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(null)
              return
            }

            resolve(
              new File([blob], fileName, {
                type: fileType,
                lastModified: Date.now(),
              }),
            )
          },
          fileType,
          quality,
        )
      })
    }

    const getFileNameForType = (fileName: string, mimeType: string): string => {
      const nameWithoutExtension = fileName.replace(/\.[^/.]+$/, '')

      if (mimeType === 'image/jpeg') {
        return `${nameWithoutExtension}.jpg`
      }

      if (mimeType === 'image/webp') {
        return `${nameWithoutExtension}.webp`
      }

      return fileName
    }

    const getCroppedFileWithinSizeLimit = async (
      canvas: HTMLCanvasElement,
      sourceFile: File,
    ): Promise<{ file: File | null; error?: string }> => {
      const initialQuality = 0.9
      const minimumQuality = 0.2
      const qualityStep = 0.05
      let lastAttemptedFile: File | null = null
      let iteration = 0

      const mimeTypesToTry = ['image/jpeg']
      if (!mimeTypesToTry.includes(sourceFile.type)) {
        mimeTypesToTry.push(sourceFile.type)
      }

      console.log('Crop compression started', {
        sourceName: sourceFile.name,
        sourceType: sourceFile.type,
        sourceSize: sourceFile.size,
        maxSizeLimit: getFileSizeLimit(sourceFile),
        mimeTypesToTry,
        initialQuality,
        minimumQuality,
        qualityStep,
      })

      for (const mimeType of mimeTypesToTry) {
        let quality = initialQuality

        while (quality >= minimumQuality) {
          iteration += 1
          const candidateFile = await canvasToFile(
            canvas,
            getFileNameForType(sourceFile.name, mimeType),
            mimeType,
            quality,
          )

          if (!candidateFile) {
            return {
              file: null,
              error: 'Failed to process cropped image.',
            }
          }

          lastAttemptedFile = candidateFile
          const sizeLimit = getFileSizeLimit(candidateFile)

          console.log('Crop compression attempt', {
            iteration,
            mimeType,
            quality,
            outputName: candidateFile.name,
            outputType: candidateFile.type,
            outputSize: candidateFile.size,
            maxSizeLimit: sizeLimit,
            fitsLimit: candidateFile.size <= sizeLimit,
          })

          if (candidateFile.size <= sizeLimit) {
            console.log('Crop compression success', {
              iteration,
              outputName: candidateFile.name,
              outputType: candidateFile.type,
              outputSize: candidateFile.size,
              maxSizeLimit: sizeLimit,
            })
            return {
              file: candidateFile,
            }
          }

          quality = Number((quality - qualityStep).toFixed(2))
        }
      }

      console.log('Crop compression failed', {
        iterations: iteration,
        lastAttemptedSize: lastAttemptedFile?.size,
        lastAttemptedType: lastAttemptedFile?.type,
        lastAttemptedName: lastAttemptedFile?.name,
        maxSizeLimit: getFileSizeLimit(lastAttemptedFile || sourceFile),
      })

      return {
        file: null,
        error: getFileSizeErrorMessage(lastAttemptedFile || sourceFile),
      }
    }

    const validateFile = (file: File): { valid: boolean; error?: string } => {
      console.log('Validating file:', {
        name: file.name,
        size: file.size,
        type: file.type,
        accept: props.accept,
        maxSize: props.maxSize,
      })

      // Check file size - for images, use 5MB limit
      const maxSizeLimit = getFileSizeLimit(file)

      if (file.size > maxSizeLimit) {
        const errorMsg = getFileSizeErrorMessage(file)
        console.log('File size validation failed:', errorMsg)
        return { valid: false, error: errorMsg }
      }

      // Check file type if accept is specified
      if (props.accept) {
        const acceptedTypes = props.accept
          .split(',')
          .map(type => type.trim())
        const fileType = file.type
        const fileName = file.name.toLowerCase()

        const isValid = acceptedTypes.some((acceptedType) => {
          if (acceptedType.startsWith('.')) {
            return fileName.endsWith(acceptedType.toLowerCase())
          }
          else if (acceptedType.includes('*')) {
            const baseType = acceptedType.split('/')[0]
            return fileType.startsWith(baseType)
          }
          else {
            return fileType === acceptedType
          }
        })

        if (!isValid) {
          const errorMsg = `File type not allowed. Accepted types: ${formatAcceptedTypes.value}`
          console.log('File type validation failed:', errorMsg)
          return { valid: false, error: errorMsg }
        }
      }

      console.log('File validation passed')
      return { valid: true }
    }

    // Auto-upload functionality
    const uploadFileToS3 = async (file: File): Promise<number | null> => {
      if (!props.autoUpload) return null

      try {
        isUploading.value = true
        const { $api, $store } = getContext()

        // Get promoter OID from store
        const promoterOid = $store.state.authModule.account?.promoterOid
        if (!promoterOid) {
          throw new Error('Promoter OID not found')
        }

        // Determine asset type based on file type
        const assetType = isVideoFile(file)
          ? props.assetType + '-video'
          : props.assetType + '-image'

        // Step 1: Get bucket with upload parameters
        const bucketResponse = await $api.buckets.createLocalBucket(
          promoterOid,
          assetType,
          file.type,
          null, // campaignOid - eventually null for now
          null, // eventOid - eventually null for now
        )

        console.log('Bucket created successfully:', bucketResponse)

        // Step 2: Upload file to S3 using the provided uploadUrl
        const formData = new FormData()

        // Add all the required fields from uploadParams
        const uploadParams = bucketResponse.uploadParams
        if (uploadParams.key) formData.append('key', uploadParams.key)
        if (uploadParams.policy) formData.append('policy', uploadParams.policy)
        if (uploadParams.xAmzCredential)
          formData.append('x-amz-credential', uploadParams.xAmzCredential)
        if (uploadParams.xAmzAlgorithm)
          formData.append('x-amz-algorithm', uploadParams.xAmzAlgorithm)
        if (uploadParams.xAmzDate)
          formData.append('x-amz-date', uploadParams.xAmzDate)
        if (uploadParams.xAmzSignature)
          formData.append('x-amz-signature', uploadParams.xAmzSignature)
        if (uploadParams.acl) formData.append('acl', uploadParams.acl)
        if (uploadParams.successActionStatus)
          formData.append(
            'success_action_status',
            uploadParams.successActionStatus,
          )
        if (uploadParams.contentType)
          formData.append('Content-Type', uploadParams.contentType)

        // Add the file last
        formData.append('file', file)

        // Upload to S3
        console.log(
          'Starting S3 upload to:',
          uploadParams.action || uploadParams.uploadUrl,
        )
        const uploadResponse = await fetch(
          uploadParams.action || uploadParams.uploadUrl,
          {
            method: 'POST',
            body: formData,
          },
        )

        if (!uploadResponse.ok) {
          const errorText = await uploadResponse
            .text()
            .catch(() => 'Unable to read error response')
          console.error('S3 upload failed:', {
            status: uploadResponse.status,
            statusText: uploadResponse.statusText,
            responseText: errorText,
            url: uploadParams.action || uploadParams.uploadUrl,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
          })
          throw new Error(
            `Upload failed with status: ${uploadResponse.status} - ${errorText}`,
          )
        }

        console.log('S3 upload successful')

        // Store the uploaded URL for internal tracking
        if (isVideoFile(file)) {
          uploadedVideoUrls.value.push(bucketResponse.url)
          uploadedUrlSizes.value.set(bucketResponse.url, file.size)
        }
        else {
          uploadedFileUrls.value.push(bucketResponse.url)
          uploadedUrlSizes.value.set(bucketResponse.url, file.size)
        }

        emit('upload-success', {
          resourceId: bucketResponse.oid,
          url: bucketResponse.url,
          filename: bucketResponse.filename,
        })

        return bucketResponse.oid
      }
      catch (error) {
        const { $store } = getContext()
        console.error('File upload failed:', {
          error: error.message || error,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          assetType: props.assetType,
          promoterOid: $store?.state?.authModule?.account?.promoterOid,
          stack: error.stack,
        })
        emit('upload-error', error)
        return null
      }
      finally {
        isUploading.value = false
      }
    }

    // Image cropping functions
    /**
     * Check if an image file matches the target frame ratio
     * Returns a promise that resolves to true if ratio matches (within tolerance), false otherwise
     */
    const checkImageMatchesRatio = async (file: File): Promise<boolean> => {
      // Only check ratio matching for ratio-based frames (e.g., "16:9")
      if (!props.frame || !props.frame.includes(':')) {
        return false // Always reframe for pixel-based frames
      }

      return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => {
          const [targetW, targetH] = props.frame!.split(':').map(Number)
          const targetRatio = targetW / targetH
          const imageRatio = img.width / img.height

          // Allow 1% tolerance for ratio matching
          const tolerance = 0.01
          const ratioMatches
            = Math.abs(imageRatio - targetRatio) / targetRatio <= tolerance

          resolve(ratioMatches)
        }
        img.onerror = () => resolve(false)
        img.src = URL.createObjectURL(file)
      })
    }

    /**
     * Process an image file directly without reframing
     * Used when the image already matches the target ratio
     */
    const processImageWithoutReframe = async (file: File) => {
      // Load image to get dimensions
      const img = new Image()
      img.onload = () => {
        // Store dimensions for quality optimization
        originalImageDimensions.value = {
          width: img.width,
          height: img.height,
        }

        // Emit the file as-is since it already matches the ratio
        if (props.multiple) {
          const currentFiles = Array.isArray(props.modelValue)
            ? props.modelValue
            : []
          const updatedFiles = [...currentFiles, file]
          console.log('processImageWithoutReframe (multiple)', updatedFiles)
          emit('update:modelValue', updatedFiles)
          emit('change', updatedFiles)
        }
        else {
          console.log('processImageWithoutReframe (single)', file)
          emit('update:modelValue', file)
          emit('change', file)
        }

        // Auto-upload if enabled
        if (props.autoUpload) {
          uploadFileToS3(file).then((resourceId) => {
            if (resourceId) {
              const uploadedUrl
                = uploadedFileUrls.value[uploadedFileUrls.value.length - 1]
              if (props.multiple) {
                const currentFiles = Array.isArray(props.modelValue)
                  ? props.modelValue
                  : []
                const index = currentFiles.indexOf(file)
                if (index !== -1) {
                  const updatedFiles = [...currentFiles]
                  updatedFiles[index] = uploadedUrl
                  emit('update:modelValue', updatedFiles)
                }
              }
              else {
                emit('update:modelValue', uploadedUrl)
              }
            }
          })
        }

        // Reset dimensions after processing
        originalImageDimensions.value = null
      }
      img.src = URL.createObjectURL(file)
    }

    const startReframeQueue = async (imagesToReframe: File[]) => {
      // Filter out images that already match the target ratio
      const imagesToProcess: File[] = []
      const imagesToSkip: File[] = []

      for (const file of imagesToReframe) {
        const matches = await checkImageMatchesRatio(file)
        if (matches) {
          imagesToSkip.push(file)
        }
        else {
          imagesToProcess.push(file)
        }
      }

      // Process images that already match the ratio
      for (const file of imagesToSkip) {
        await processImageWithoutReframe(file)
      }

      // Queue remaining images for reframing
      reframeQueue.value = [...imagesToProcess]
      currentReframeIndex.value = 0

      if (reframeQueue.value.length > 0) {
        openReframeModal(reframeQueue.value[0])
      }
    }

    const openReframeModal = async (file: File) => {
      // Load the image once and reuse it for both the ratio check and canvas
      // rendering. Previously the image was loaded twice (once in
      // checkImageMatchesRatio, once in a separate img.onload), and the second
      // load could fail silently in headless CI, leaving originalImage null and
      // the Apply Crop button permanently disabled.
      const img = await new Promise<HTMLImageElement | null>((resolve) => {
        const image = new Image()
        image.onload = () => resolve(image)
        image.onerror = () => resolve(null)
        image.src = URL.createObjectURL(file)
      })

      if (!img) return

      // Check if image already matches the target ratio
      if (props.frame && props.frame.includes(':')) {
        const [targetW, targetH] = props.frame.split(':').map(Number)
        const targetRatio = targetW / targetH
        const imageRatio = img.width / img.height
        const tolerance = 0.01
        const ratioMatches
          = Math.abs(imageRatio - targetRatio) / targetRatio <= tolerance

        if (ratioMatches) {
          await processImageWithoutReframe(file)
          return
        }
      }

      // Set image state before showing the modal so the Apply Crop button
      // renders enabled immediately when the modal first appears.
      originalFile.value = file
      originalImage.value = img
      originalImageDimensions.value = { width: img.width, height: img.height }
      showReframeModal.value = true

      // Wait for Vue to mount the canvas DOM element before drawing
      await nextTick()
      setupCanvas(img)
    }

    const setupCanvas = (img: HTMLImageElement) => {
      if (!cropCanvas.value) return

      const canvas = cropCanvas.value
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      // Set fixed canvas size
      canvasSize.value = { width: 600, height: 400 }
      canvas.width = canvasSize.value.width
      canvas.height = canvasSize.value.height

      // Calculate crop area size first to fit within canvas with padding
      const canvasPadding = 40 // Minimum padding from canvas edges
      const maxCropWidth = canvas.width - canvasPadding * 2
      const maxCropHeight = canvas.height - canvasPadding * 2

      // Calculate scale to fit the frame within available space
      const frameAspectRatio = frameHeight.value / frameWidth.value
      let cropDisplayWidth, cropDisplayHeight

      if (maxCropWidth * frameAspectRatio <= maxCropHeight) {
        // Width is the limiting factor
        cropDisplayWidth = maxCropWidth
        cropDisplayHeight = maxCropWidth * frameAspectRatio
      }
      else {
        // Height is the limiting factor
        cropDisplayHeight = maxCropHeight
        cropDisplayWidth = cropDisplayHeight / frameAspectRatio
      }

      // Position crop area in center of canvas (ensuring it fits)
      const canvasCenterX = canvas.width / 2
      const canvasCenterY = canvas.height / 2

      cropArea.value = {
        x: canvasCenterX - cropDisplayWidth / 2,
        y: canvasCenterY - cropDisplayHeight / 2,
        width: cropDisplayWidth,
        height: cropDisplayHeight,
      }

      // Now calculate initial image scale to cover the crop area completely (no blank areas)
      const scaleX = cropDisplayWidth / img.width
      const scaleY = cropDisplayHeight / img.height
      imageScale.value = Math.max(scaleX, scaleY) // Use max to ensure no blank areas

      // Center image initially, but apply constraints to ensure frame is always covered
      let initialX = (canvas.width - img.width * imageScale.value) / 2
      let initialY = (canvas.height - img.height * imageScale.value) / 2

      // Calculate constraints for initial positioning
      const scaledImageWidth = img.width * imageScale.value
      const scaledImageHeight = img.height * imageScale.value
      const minX = cropArea.value.x + cropArea.value.width - scaledImageWidth
      const maxX = cropArea.value.x
      const minY = cropArea.value.y + cropArea.value.height - scaledImageHeight
      const maxY = cropArea.value.y

      // Apply constraints to initial position
      initialX = Math.max(minX, Math.min(maxX, initialX))
      initialY = Math.max(minY, Math.min(maxY, initialY))

      imageOffset.value = {
        x: initialX,
        y: initialY,
      }

      // Ensure crop area stays within canvas bounds (it should by design, but safety check)
      cropArea.value.x = Math.max(
        0,
        Math.min(cropArea.value.x, canvas.width - cropArea.value.width),
      )
      cropArea.value.y = Math.max(
        0,
        Math.min(cropArea.value.y, canvas.height - cropArea.value.height),
      )

      // Ensure crop area is within image bounds
      const imageBounds = getImageBounds()
      if (imageBounds) {
        cropArea.value.x = Math.max(
          imageBounds.left,
          Math.min(cropArea.value.x, imageBounds.right - cropArea.value.width),
        )
        cropArea.value.y = Math.max(
          imageBounds.top,
          Math.min(
            cropArea.value.y,
            imageBounds.bottom - cropArea.value.height,
          ),
        )
      }

      drawCanvas()
    }

    const drawCanvas = () => {
      if (!cropCanvas.value || !originalImage.value) return

      const canvas = cropCanvas.value
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Draw the scaled and positioned image
      const scaledWidth = originalImage.value.width * imageScale.value
      const scaledHeight = originalImage.value.height * imageScale.value

      ctx.drawImage(
        originalImage.value,
        imageOffset.value.x,
        imageOffset.value.y,
        scaledWidth,
        scaledHeight,
      )

      // Draw dark overlay everywhere
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Clear the crop area (no overlay)
      ctx.clearRect(
        cropArea.value.x,
        cropArea.value.y,
        cropArea.value.width,
        cropArea.value.height,
      )

      // Redraw image in crop area only
      ctx.save()
      ctx.beginPath()
      ctx.rect(
        cropArea.value.x,
        cropArea.value.y,
        cropArea.value.width,
        cropArea.value.height,
      )
      ctx.clip()

      ctx.drawImage(
        originalImage.value,
        imageOffset.value.x,
        imageOffset.value.y,
        scaledWidth,
        scaledHeight,
      )
      ctx.restore()

      // Draw crop border
      ctx.strokeStyle = '#9333EA'
      ctx.lineWidth = 2
      ctx.strokeRect(
        cropArea.value.x,
        cropArea.value.y,
        cropArea.value.width,
        cropArea.value.height,
      )

      // Add corner indicators
      const cornerSize = 8
      ctx.fillStyle = '#9333EA'
      // Top-left corner
      ctx.fillRect(
        cropArea.value.x - cornerSize / 2,
        cropArea.value.y - cornerSize / 2,
        cornerSize,
        cornerSize,
      )
      // Top-right corner
      ctx.fillRect(
        cropArea.value.x + cropArea.value.width - cornerSize / 2,
        cropArea.value.y - cornerSize / 2,
        cornerSize,
        cornerSize,
      )
      // Bottom-left corner
      ctx.fillRect(
        cropArea.value.x - cornerSize / 2,
        cropArea.value.y + cropArea.value.height - cornerSize / 2,
        cornerSize,
        cornerSize,
      )
      // Bottom-right corner
      ctx.fillRect(
        cropArea.value.x + cropArea.value.width - cornerSize / 2,
        cropArea.value.y + cropArea.value.height - cornerSize / 2,
        cornerSize,
        cornerSize,
      )
    }

    // Helper function to get current image boundaries on canvas
    const getImageBounds = () => {
      if (!originalImage.value) return null

      const scaledWidth = originalImage.value.width * imageScale.value
      const scaledHeight = originalImage.value.height * imageScale.value

      return {
        left: imageOffset.value.x,
        top: imageOffset.value.y,
        right: imageOffset.value.x + scaledWidth,
        bottom: imageOffset.value.y + scaledHeight,
        width: scaledWidth,
        height: scaledHeight,
      }
    }

    const startDrag = (event: MouseEvent) => {
      const canvas = cropCanvas.value
      if (!canvas) return

      const rect = canvas.getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top

      // Always drag the crop frame, never the image
      isDragging.value = true
      dragMode.value = 'crop'
      dragStart.value = {
        x: x - cropArea.value.x,
        y: y - cropArea.value.y,
      }
    }

    const updateDrag = (event: MouseEvent) => {
      if (!isDragging.value || !cropCanvas.value || !originalImage.value)
        return

      const canvas = cropCanvas.value
      const rect = canvas.getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top

      // Calculate desired new position for the crop frame
      let newX = x - dragStart.value.x
      let newY = y - dragStart.value.y

      // Keep the crop frame within the canvas bounds
      newX = Math.max(0, Math.min(newX, canvas.width - cropArea.value.width))
      newY = Math.max(0, Math.min(newY, canvas.height - cropArea.value.height))

      // Keep the crop frame fully covered by the (fixed) image
      const imageBounds = getImageBounds()
      if (imageBounds) {
        newX = Math.max(
          imageBounds.left,
          Math.min(newX, imageBounds.right - cropArea.value.width),
        )
        newY = Math.max(
          imageBounds.top,
          Math.min(newY, imageBounds.bottom - cropArea.value.height),
        )
      }

      cropArea.value.x = newX
      cropArea.value.y = newY

      drawCanvas()
    }

    const endDrag = () => {
      isDragging.value = false
    }

    const handleZoom = (event: WheelEvent) => {
      event.preventDefault()

      const zoomFactor = event.deltaY > 0 ? 0.9 : 1.1
      const newScale = imageScale.value * zoomFactor

      // Limit zoom range
      if (newScale < 0.1 || newScale > 5) return

      // If zooming out, check if the image would become smaller than the crop area
      if (event.deltaY > 0 && originalImage.value) {
        const newImageWidth = originalImage.value.width * newScale
        const newImageHeight = originalImage.value.height * newScale

        if (
          newImageWidth < cropArea.value.width
          || newImageHeight < cropArea.value.height
        ) {
          return // Don't zoom out if image would be smaller than crop area
        }
      }

      const canvas = cropCanvas.value
      if (!canvas || !originalImage.value) return

      // Get mouse position relative to canvas
      const rect = canvas.getBoundingClientRect()
      const mouseX = event.clientX - rect.left
      const mouseY = event.clientY - rect.top

      // Calculate zoom center (prefer crop area center, fallback to mouse)
      const zoomCenterX = cropArea.value.x + cropArea.value.width / 2
      const zoomCenterY = cropArea.value.y + cropArea.value.height / 2

      // Calculate new image dimensions
      const oldWidth = originalImage.value.width * imageScale.value
      const oldHeight = originalImage.value.height * imageScale.value
      const newWidth = originalImage.value.width * newScale
      const newHeight = originalImage.value.height * newScale

      // Adjust offset to zoom towards center
      imageOffset.value.x
        = imageOffset.value.x
          - (newWidth - oldWidth)
          * ((zoomCenterX - imageOffset.value.x) / oldWidth)
      imageOffset.value.y
        = imageOffset.value.y
          - (newHeight - oldHeight)
          * ((zoomCenterY - imageOffset.value.y) / oldHeight)

      imageScale.value = newScale

      // After zooming, ensure crop area is still within image bounds
      const imageBounds = getImageBounds()
      if (imageBounds) {
        // Adjust crop area position if it's now outside the image
        let adjustedX = cropArea.value.x
        let adjustedY = cropArea.value.y

        adjustedX = Math.max(
          imageBounds.left,
          Math.min(adjustedX, imageBounds.right - cropArea.value.width),
        )
        adjustedY = Math.max(
          imageBounds.top,
          Math.min(adjustedY, imageBounds.bottom - cropArea.value.height),
        )

        cropArea.value.x = adjustedX
        cropArea.value.y = adjustedY
      }

      drawCanvas()
    }

    const zoomIn = () => {
      if (!originalImage.value || !cropCanvas.value) return

      const zoomFactor = 1.1
      const newScale = imageScale.value * zoomFactor

      // Limit zoom range
      if (newScale > 5) return

      performZoom(newScale)
    }

    const zoomOut = () => {
      if (!originalImage.value || !cropCanvas.value) return

      const zoomFactor = 0.9
      const newScale = imageScale.value * zoomFactor

      // Limit zoom range
      if (newScale < 0.1) return

      // Check if the image would become smaller than needed to fill the crop area
      // Calculate minimum scale needed to fill crop area
      const minScaleX = cropArea.value.width / originalImage.value.width
      const minScaleY = cropArea.value.height / originalImage.value.height
      const minScale = Math.max(minScaleX, minScaleY)

      if (newScale < minScale) {
        return // Don't zoom out if image would be smaller than needed to fill crop area
      }

      performZoom(newScale)
    }

    const performZoom = (newScale: number) => {
      if (!originalImage.value || !cropCanvas.value) return

      // Calculate zoom center (crop area center)
      const zoomCenterX = cropArea.value.x + cropArea.value.width / 2
      const zoomCenterY = cropArea.value.y + cropArea.value.height / 2

      // Calculate new image dimensions
      const oldWidth = originalImage.value.width * imageScale.value
      const oldHeight = originalImage.value.height * imageScale.value
      const newWidth = originalImage.value.width * newScale
      const newHeight = originalImage.value.height * newScale

      // Adjust offset to zoom towards center
      imageOffset.value.x
        = imageOffset.value.x
          - (newWidth - oldWidth)
          * ((zoomCenterX - imageOffset.value.x) / oldWidth)
      imageOffset.value.y
        = imageOffset.value.y
          - (newHeight - oldHeight)
          * ((zoomCenterY - imageOffset.value.y) / oldHeight)

      imageScale.value = newScale

      // After scaling, apply constraints to ensure frame is always covered
      const minX = cropArea.value.x + cropArea.value.width - newWidth
      const maxX = cropArea.value.x
      const minY = cropArea.value.y + cropArea.value.height - newHeight
      const maxY = cropArea.value.y

      // Apply constraints to current position after zoom
      imageOffset.value.x = Math.max(minX, Math.min(maxX, imageOffset.value.x))
      imageOffset.value.y = Math.max(minY, Math.min(maxY, imageOffset.value.y))

      drawCanvas()
    }

    const applyCrop = async () => {
      if (!originalImage.value || !cropCanvas.value || !originalFile.value)
        return

      // Create a new canvas for the cropped image
      const outputCanvas = document.createElement('canvas')
      const outputCtx = outputCanvas.getContext('2d')
      if (!outputCtx) return

      outputCanvas.width = frameWidth.value
      outputCanvas.height = frameHeight.value

      // Calculate which part of the original image is visible in the crop area
      // Convert crop area coordinates to original image coordinates

      // First, determine what part of the original image corresponds to the crop area
      const cropAreaInImageX
        = (cropArea.value.x - imageOffset.value.x) / imageScale.value
      const cropAreaInImageY
        = (cropArea.value.y - imageOffset.value.y) / imageScale.value
      const cropAreaInImageWidth = cropArea.value.width / imageScale.value
      const cropAreaInImageHeight = cropArea.value.height / imageScale.value

      // Ensure we don't go outside image bounds
      const sourceX = Math.max(0, cropAreaInImageX)
      const sourceY = Math.max(0, cropAreaInImageY)
      const sourceWidth = Math.min(
        cropAreaInImageWidth,
        originalImage.value.width - sourceX,
      )
      const sourceHeight = Math.min(
        cropAreaInImageHeight,
        originalImage.value.height - sourceY,
      )

      // Draw cropped area to output canvas, scaled to exact frame dimensions
      outputCtx.drawImage(
        originalImage.value,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        frameWidth.value,
        frameHeight.value,
      )

      const { file: croppedFile, error } = await getCroppedFileWithinSizeLimit(
        outputCanvas,
        originalFile.value,
      )

      if (!croppedFile) {
        validationState.value
          = error || getFileSizeErrorMessage(originalFile.value)
        return
      }

      resetValidation()

      // Handle multiple vs single file mode
      if (props.multiple && reframeQueue.value.length > 0) {
        // Add cropped file to the existing files
        const currentFiles = Array.isArray(props.modelValue)
          ? props.modelValue
          : []
        const updatedFiles = [...currentFiles, croppedFile]
        console.log('applyCrop', updatedFiles)
        emit('update:modelValue', updatedFiles)
        emit('change', updatedFiles)
        emit('reframe-complete', croppedFile)

        // Auto-upload if enabled
        if (props.autoUpload) {
          uploadFileToS3(croppedFile).then((resourceId) => {
            if (resourceId) {
              // Replace the last file with its URL in modelValue
              const currentFiles = Array.isArray(props.modelValue)
                ? props.modelValue
                : []
              const newFiles = [...currentFiles]
              const uploadedUrl = isVideoFile(croppedFile)
                ? uploadedVideoUrls.value[uploadedVideoUrls.value.length - 1]
                : uploadedFileUrls.value[uploadedFileUrls.value.length - 1]
              newFiles[newFiles.length - 1] = uploadedUrl
              emit('update:modelValue', newFiles)
            }
          })
        }

        // Move to next image in queue
        currentReframeIndex.value++
        if (currentReframeIndex.value < reframeQueue.value.length) {
          // Open next image for reframing
          openReframeModal(reframeQueue.value[currentReframeIndex.value])
          return // Don't close modal yet
        }
        else {
          // All images processed, clear queue and close modal
          reframeQueue.value = []
          currentReframeIndex.value = 0
        }
      }
      else {
        // Single file mode (existing behavior)
        console.log('applyCrop', croppedFile)
        emit('update:modelValue', croppedFile)
        emit('change', croppedFile)
        emit('reframe-complete', croppedFile)

        // Auto-upload if enabled
        if (props.autoUpload) {
          uploadFileToS3(croppedFile).then((resourceId) => {
            if (resourceId) {
              // Replace the file with its URL in modelValue
              const uploadedUrl = isVideoFile(croppedFile)
                ? uploadedVideoUrls.value[uploadedVideoUrls.value.length - 1]
                : uploadedFileUrls.value[uploadedFileUrls.value.length - 1]
              emit('update:modelValue', uploadedUrl)
            }
          })
        }
      }

      closeReframeModal()
    }

    const closeReframeModal = () => {
      showReframeModal.value = false
      originalFile.value = null
      originalImage.value = null
      originalImageDimensions.value = null
      cropArea.value = { x: 0, y: 0, width: 0, height: 0 }
      isDragging.value = false
      dragMode.value = 'crop'
      dragStart.value = { x: 0, y: 0 }
      imageScale.value = 1
      imageOffset.value = { x: 0, y: 0 }

      // Clear reframe queue when modal is closed/cancelled
      reframeQueue.value = []
      currentReframeIndex.value = 0
    }

    // Event handlers
    const handleFileSelect = (event: Event) => {
      const target = event.target as HTMLInputElement
      const files = target.files

      if (files && files.length > 0) {
        const fileArray = Array.from(files)
        const validFiles: File[] = []
        let validationError = ''

        // Validate each file and collect errors
        for (const file of fileArray) {
          const validation = validateFile(file)
          if (validation.valid) {
            validFiles.push(file)
          }
          else if (validation.error && !validationError) {
            // Show the first validation error
            validationError = validation.error
          }
        }

        // If there's a validation error, show it and stop processing
        if (validationError) {
          validationState.value = validationError
          console.log('File validation error:', validationError)
          // Don't update model value or emit change when there are validation errors
          return
        }
        else {
          // Clear any previous validation errors
          resetValidation()
        }

        // Only update model value if validation passed
        if (props.multiple) {
          // Check if reframing is enabled and there are image files to reframe
          if (props.reframe) {
            const imagesToReframe = validFiles.filter(file =>
              isImageFile(file),
            )
            const nonImages = validFiles.filter(file => !isImageFile(file))

            if (imagesToReframe.length > 0) {
              // Add non-image files immediately to the model
              if (nonImages.length > 0) {
                const currentFiles = Array.isArray(props.modelValue)
                  ? props.modelValue
                  : []
                const updatedFiles = [...currentFiles, ...nonImages]
                console.log('handleFileSelect', updatedFiles)
                emit('update:modelValue', updatedFiles)
                emit('change', updatedFiles)

                // Auto-upload non-image files if enabled
                if (props.autoUpload) {
                  nonImages.forEach(async (file) => {
                    const resourceId = await uploadFileToS3(file)
                    if (resourceId) {
                      // Replace the file with its URL in modelValue
                      const currentFiles = Array.isArray(props.modelValue)
                        ? props.modelValue
                        : []
                      const fileIndex = currentFiles.findIndex(
                        f => f === file,
                      )
                      if (fileIndex !== -1) {
                        const newFiles = [...currentFiles]
                        const uploadedUrl = isVideoFile(file)
                          ? uploadedVideoUrls.value[
                            uploadedVideoUrls.value.length - 1
                          ]
                          : uploadedFileUrls.value[
                            uploadedFileUrls.value.length - 1
                          ]
                        newFiles[fileIndex] = uploadedUrl
                        emit('update:modelValue', newFiles)
                      }
                    }
                  })
                }
              }

              // Queue image files for reframing
              startReframeQueue(imagesToReframe)
              return // Don't process further until reframing is done
            }
          }

          // No reframing needed, add all files normally
          const currentFiles = Array.isArray(props.modelValue)
            ? props.modelValue
            : []
          const newFiles = [...currentFiles, ...validFiles]
          console.log('handleFileSelect', newFiles)
          emit('update:modelValue', newFiles)
          emit('change', newFiles)

          // Auto-upload multiple files if enabled
          if (props.autoUpload) {
            validFiles.forEach(async (file) => {
              const resourceId = await uploadFileToS3(file)
              if (resourceId) {
                // Replace the file with its URL in modelValue
                const currentFiles = Array.isArray(props.modelValue)
                  ? props.modelValue
                  : []
                const fileIndex = currentFiles.findIndex(f => f === file)
                if (fileIndex !== -1) {
                  const newFiles = [...currentFiles]
                  const uploadedUrl = isVideoFile(file)
                    ? uploadedVideoUrls.value[
                      uploadedVideoUrls.value.length - 1
                    ]
                    : uploadedFileUrls.value[uploadedFileUrls.value.length - 1]
                  newFiles[fileIndex] = uploadedUrl
                  emit('update:modelValue', newFiles)
                }
              }
            })
          }
        }
        else {
          const newFile = validFiles[0] || null

          // Check if reframing is enabled and file is an image
          if (newFile && props.reframe && isImageFile(newFile)) {
            openReframeModal(newFile)
            return // Don't emit until after cropping
          }

          console.log('handleFileSelect', newFile)
          emit('update:modelValue', newFile)
          emit('change', newFile)

          // Auto-upload single file if enabled
          if (newFile && props.autoUpload) {
            uploadFileToS3(newFile).then((resourceId) => {
              if (resourceId) {
                // Replace the file with its URL in modelValue
                const uploadedUrl = isVideoFile(newFile)
                  ? uploadedVideoUrls.value[uploadedVideoUrls.value.length - 1]
                  : uploadedFileUrls.value[uploadedFileUrls.value.length - 1]
                emit('update:modelValue', uploadedUrl)
              }
            })
          }
        }

        if (props.validateOnChange) {
          console.log('validateOnChange', files, props.required)
          _runValidation(files, props.required)
        }
      }

      // Reset input value to allow selecting the same file again
      target.value = ''
    }

    const handleDragOver = (event: DragEvent) => {
      if (props.disabled) return
      isDragOver.value = true
    }

    const handleDragLeave = (event: DragEvent) => {
      if (props.disabled) return
      isDragOver.value = false
    }

    const handleDrop = (event: DragEvent) => {
      if (props.disabled) return
      isDragOver.value = false

      const files = event.dataTransfer?.files
      if (files && files.length > 0) {
        const fileArray = Array.from(files)
        const validFiles: File[] = []
        let validationError = ''

        // Validate each file and collect errors
        for (const file of fileArray) {
          const validation = validateFile(file)
          if (validation.valid) {
            validFiles.push(file)
          }
          else if (validation.error && !validationError) {
            // Show the first validation error
            validationError = validation.error
          }
        }

        // If there's a validation error, show it and stop processing
        if (validationError) {
          validationState.value = validationError
          console.log('File validation error:', validationError)
          // Don't update model value or emit change when there are validation errors
          return
        }
        else {
          // Clear any previous validation errors
          resetValidation()
        }

        // Only update model value if validation passed
        if (props.multiple) {
          // Check if reframing is enabled and there are image files to reframe
          if (props.reframe) {
            const imagesToReframe = validFiles.filter(file =>
              isImageFile(file),
            )
            const nonImages = validFiles.filter(file => !isImageFile(file))

            if (imagesToReframe.length > 0) {
              // Add non-image files immediately to the model
              if (nonImages.length > 0) {
                const currentFiles = Array.isArray(props.modelValue)
                  ? props.modelValue
                  : []
                const updatedFiles = [...currentFiles, ...nonImages]
                console.log('handleDrop', updatedFiles)
                emit('update:modelValue', updatedFiles)
                emit('change', updatedFiles)

                // Auto-upload non-image files if enabled
                if (props.autoUpload) {
                  nonImages.forEach(async (file) => {
                    const resourceId = await uploadFileToS3(file)
                    if (resourceId) {
                      // Replace the file with its URL in modelValue
                      const currentFiles = Array.isArray(props.modelValue)
                        ? props.modelValue
                        : []
                      const fileIndex = currentFiles.findIndex(
                        f => f === file,
                      )
                      if (fileIndex !== -1) {
                        const newFiles = [...currentFiles]
                        const uploadedUrl = isVideoFile(file)
                          ? uploadedVideoUrls.value[
                            uploadedVideoUrls.value.length - 1
                          ]
                          : uploadedFileUrls.value[
                            uploadedFileUrls.value.length - 1
                          ]
                        newFiles[fileIndex] = uploadedUrl
                        emit('update:modelValue', newFiles)
                      }
                    }
                  })
                }
              }

              // Queue image files for reframing
              startReframeQueue(imagesToReframe)
              return // Don't process further until reframing is done
            }
          }

          // No reframing needed, add all files normally
          const currentFiles = Array.isArray(props.modelValue)
            ? props.modelValue
            : []
          const newFiles = [...currentFiles, ...validFiles]
          console.log('handleDrop', newFiles)
          emit('update:modelValue', newFiles)
          emit('change', newFiles)

          // Auto-upload multiple files if enabled
          if (props.autoUpload) {
            validFiles.forEach(async (file) => {
              const resourceId = await uploadFileToS3(file)
              if (resourceId) {
                // Replace the file with its URL in modelValue
                const currentFiles = Array.isArray(props.modelValue)
                  ? props.modelValue
                  : []
                const fileIndex = currentFiles.findIndex(f => f === file)
                if (fileIndex !== -1) {
                  const newFiles = [...currentFiles]
                  const uploadedUrl = isVideoFile(file)
                    ? uploadedVideoUrls.value[
                      uploadedVideoUrls.value.length - 1
                    ]
                    : uploadedFileUrls.value[uploadedFileUrls.value.length - 1]
                  newFiles[fileIndex] = uploadedUrl
                  emit('update:modelValue', newFiles)
                }
              }
            })
          }
        }
        else {
          const newFile = validFiles[0] || null

          // Check if reframing is enabled and file is an image
          if (newFile && props.reframe && isImageFile(newFile)) {
            openReframeModal(newFile)
            return // Don't emit until after cropping
          }

          console.log('handleDrop', newFile)
          emit('update:modelValue', newFile)
          emit('change', newFile)

          // Auto-upload single file if enabled
          if (newFile && props.autoUpload) {
            uploadFileToS3(newFile).then((resourceId) => {
              if (resourceId) {
                // Replace the file with its URL in modelValue
                const uploadedUrl = isVideoFile(newFile)
                  ? uploadedVideoUrls.value[uploadedVideoUrls.value.length - 1]
                  : uploadedFileUrls.value[uploadedFileUrls.value.length - 1]
                emit('update:modelValue', uploadedUrl)
              }
            })
          }
        }

        if (props.validateOnChange) {
          _runValidation(props.modelValue, props.required)
        }
      }

      // Reset the file input value to allow re-selecting the same file
      if (fileInputRef.value) {
        fileInputRef.value.value = ''
      }
    }

    const removeFile = (index?: number) => {
      let newValue: File[] | null = null

      if (
        props.multiple
        && Array.isArray(props.modelValue)
        && typeof index === 'number'
      ) {
        const newFiles = [...props.modelValue]
        newFiles.splice(index, 1)
        newValue = newFiles
        emit('update:modelValue', newFiles)
        emit('change', newFiles)
      }
      else {
        newValue = null
        emit('update:modelValue', null)
        emit('change', null)
      }

      // Reset the file input value to allow re-uploading the same file
      if (fileInputRef.value) {
        fileInputRef.value.value = ''
      }

      if (props.validateOnChange) {
        _runValidation(newValue, props.required)
      }
    }

    const removeExistingFile = () => {
      // Clear both modelValue and previewUrl for existing files
      emit('update:modelValue', null)
      emit('update:previewUrl', '')
      emit('change', null)

      // Reset the file input value to allow re-uploading the same file
      if (fileInputRef.value) {
        fileInputRef.value.value = ''
      }
    }

    const removeInternalUrl = (index: number) => {
      // Get the URL to remove BEFORE modifying any arrays
      const removedUrl = internalPreviewUrls.value[index]
      if (!removedUrl) return

      // Remove from previewUrl prop if it exists there
      if (Array.isArray(props.previewUrl)) {
        const previewUrlIndex = props.previewUrl.indexOf(removedUrl)
        if (previewUrlIndex !== -1) {
          const newPreviewUrls = [...props.previewUrl]
          newPreviewUrls.splice(previewUrlIndex, 1)
          emit(
            'update:previewUrl',
            newPreviewUrls.length === 0 ? [] : newPreviewUrls,
          )
        }
      }
      else if (props.previewUrl === removedUrl) {
        emit('update:previewUrl', '')
      }

      // Remove from modelValue if it exists there (for auto-upload mode)
      if (props.autoUpload && Array.isArray(props.modelValue)) {
        const modelValueIndex = props.modelValue.indexOf(removedUrl)
        if (modelValueIndex !== -1) {
          const newModelValue = [...props.modelValue]
          newModelValue.splice(modelValueIndex, 1)
          emit('update:modelValue', newModelValue)
          emit('change', newModelValue)
        }
      }

      // Remove from internal tracking
      const urlIndex = uploadedFileUrls.value.indexOf(removedUrl)
      if (urlIndex !== -1) {
        uploadedFileUrls.value.splice(urlIndex, 1)
      }
      uploadedUrlSizes.value.delete(removedUrl)
    }

    const removeUploadedVideo = (index: number) => {
      const removedVideoUrl = uploadedVideoUrls.value[index]
      if (!removedVideoUrl) return

      // Remove from uploadedVideoUrls
      uploadedVideoUrls.value.splice(index, 1)

      // Remove from previewUrl prop if it exists there
      if (Array.isArray(props.previewUrl)) {
        const previewUrlIndex = props.previewUrl.indexOf(removedVideoUrl)
        if (previewUrlIndex !== -1) {
          const newPreviewUrls = [...props.previewUrl]
          newPreviewUrls.splice(previewUrlIndex, 1)
          emit(
            'update:previewUrl',
            newPreviewUrls.length === 0 ? [] : newPreviewUrls,
          )
        }
      }
      else if (props.previewUrl === removedVideoUrl) {
        emit('update:previewUrl', '')
      }

      // Remove from modelValue if it exists there (for auto-upload mode)
      if (props.autoUpload && Array.isArray(props.modelValue)) {
        const modelValueIndex = props.modelValue.indexOf(removedVideoUrl)
        if (modelValueIndex !== -1) {
          const newModelValue = [...props.modelValue]
          newModelValue.splice(modelValueIndex, 1)
          emit('update:modelValue', newModelValue)
          emit('change', newModelValue)
        }
      }

      // Remove associated thumbnail
      if (videoThumbnails.value.has(removedVideoUrl)) {
        videoThumbnails.value.delete(removedVideoUrl)
      }
      uploadedUrlSizes.value.delete(removedVideoUrl)
    }

    const handleDropzoneClick = () => {
      if (props.disabled) return
      fileInputRef.value?.click()
    }

    const handleReplaceClick = () => {
      if (props.disabled) return
      // Reset the file input value before opening the dialog
      // This allows re-selecting the same file
      if (fileInputRef.value) {
        fileInputRef.value.value = ''
      }
      fileInputRef.value?.click()
    }

    // Public method to run validation
    const runValidation = (
      value = props.modelValue || props.previewUrl,
      checkRequired = props.required,
    ) => {
      return _runValidation(value, checkRequired)
    }

    // Cleanup object URLs when component unmounts or modelValue changes
    watch(
      () => props.modelValue,
      (newValue, oldValue) => {
        if (oldValue) {
          revokeObjectUrls()
        }
      },
      { deep: true },
    )

    onUnmounted(() => {
      revokeObjectUrls()
    })

    return {
      inputId,
      fileInputRef,
      isDragOver,
      isUploading,
      validationState,
      formatAcceptedTypes,
      formatFileSize,
      getUploadedUrlSize,
      parseMaxSize,
      isImageFile,
      isVideoFile,
      isVideoUrl,
      getImagePreviewUrl,
      getVideoPreviewUrl,
      getVideoThumbnailForFile,
      getSingleImageWidth,
      startVideoPreview,
      stopVideoPreview,
      handleFileSelect,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      handleDropzoneClick,
      handleReplaceClick,
      removeFile,
      removeExistingFile,
      generateDataTestId,
      runValidation,
      resetValidation,
      uploadingFiles,
      pendingFiles,
      singleVideoUrl,
      singleVideoThumbnail,
      hasNoFiles,
      hasPreviewUrlOnly,
      singlePreviewUrl,
      hasSingleFile,
      hasSingleUrl,
      hasMultipleFiles,
      uploadingFilesArray,
      internalPreviewUrls,
      previewAspectRatio,
      removeInternalUrl,
      removeUploadedVideo,
      uploadedVideoUrls,
      videoThumbnails,
      // Reframing functionality
      reframeQueue,
      currentReframeIndex,
      showReframeModal,
      startReframeQueue,
      cropCanvas,
      frameWidth,
      frameHeight,
      startDrag,
      updateDrag,
      endDrag,
      handleZoom,
      zoomIn,
      zoomOut,
      applyCrop,
      closeReframeModal,
    }
  },
})
</script>
